import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  AppointmentDto,
  PropertyApplicationDto,
  PropertyApplicationPendingActionType,
  PropertyApplicationStatus,
  PropertyDto,
} from '@workspace/data-types';
import { DateTime } from 'luxon';
import { PropertyVisitDetailsDialogComponent } from './visit-details-dialog/property-visit-details-dialog.component';

export interface ApplicationTimelineStep {
  index: number;
  label: string;
  sublabel: string;
  dateBadge?: string | null;
  icon: string;
  state: 'completed' | 'current' | 'upcoming' | 'rejected' | 'cancelled';
  isClickable?: boolean;
  clickTooltip?: string;
}

@Component({
  selector: 'etxe-property-application-timeline',
  imports: [MatIconModule, MatTooltipModule, MatDialogModule],
  templateUrl: './property-application-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyApplicationTimelineComponent {
  private readonly dialog = inject(MatDialog);

  readonly status = input.required<PropertyApplicationStatus>();
  readonly application = input<PropertyApplicationDto | null>(null);
  readonly appointment = input<AppointmentDto | null>(null);
  readonly property = input<PropertyDto | null>(null);

  readonly visitClick = output<void>();

  readonly currentStepIndex = computed<number>(() => {
    const s = this.status();
    const actionType = this.application()?.pendingAction?.actionType;

    if (actionType === PropertyApplicationPendingActionType.FINAL_OFFER) {
      return 2;
    }
    if (actionType === PropertyApplicationPendingActionType.MANDATE_SELECTION) {
      return 3;
    }

    switch (s) {
      case PropertyApplicationStatus.PENDING:
        return 0;
      case PropertyApplicationStatus.VISIT_INVITED:
      case PropertyApplicationStatus.VISIT_CONFIRMED:
        return 1;
      case PropertyApplicationStatus.VISITED:
        return 2;
      case PropertyApplicationStatus.OFFER_SUBMITTED:
      case PropertyApplicationStatus.ACCEPTED:
      case PropertyApplicationStatus.REJECTED:
      case PropertyApplicationStatus.EXPIRED:
      case PropertyApplicationStatus.CANCELLED:
        return 3;
      default:
        return 0;
    }
  });

  // Formatted candidacy date
  readonly candidacyDate = computed<string | null>(() => {
    const created = this.application()?.createdAt;
    if (!created) return null;
    const dt =
      typeof created === 'string' ? DateTime.fromISO(created) : DateTime.fromJSDate(new Date(created));
    return dt.isValid ? dt.setLocale('fr').toFormat('d LLL yyyy') : null;
  });

  // Formatted visit date & time
  readonly visitDate = computed<string | null>(() => {
    const appt = this.appointment();
    const confirmed = appt?.confirmedSlot;
    if (confirmed?.startAt) {
      const dt = DateTime.fromISO(confirmed.startAt).setLocale('fr');
      return dt.isValid ? dt.toFormat("d LLL 'à' HH'h'mm") : null;
    }
    return null;
  });

  // Formatted deadline date for final offer or pending action
  readonly deadlineDate = computed<string | null>(() => {
    const deadline = this.application()?.pendingAction?.deadline;
    if (!deadline) return null;
    const dt =
      typeof deadline === 'string' ? DateTime.fromISO(deadline) : DateTime.fromJSDate(new Date(deadline));
    return dt.isValid ? dt.setLocale('fr').toFormat('d LLL yyyy') : null;
  });

  // Formatted final offer submission date
  readonly finalOfferDate = computed<string | null>(() => {
    const submittedAt = this.application()?.engagement?.finalOfferSubmittedAt;
    if (!submittedAt) return null;
    const dt =
      typeof submittedAt === 'string'
        ? DateTime.fromISO(submittedAt)
        : DateTime.fromJSDate(new Date(submittedAt));
    return dt.isValid ? dt.setLocale('fr').toFormat('d LLL yyyy') : null;
  });

  readonly steps = computed<ApplicationTimelineStep[]>(() => {
    const s = this.status();
    const isAccepted = s === PropertyApplicationStatus.ACCEPTED;
    const isRejected = s === PropertyApplicationStatus.REJECTED;
    const isCancelled = s === PropertyApplicationStatus.CANCELLED || s === PropertyApplicationStatus.EXPIRED;
    const currentIdx = this.currentStepIndex();

    const getStepState = (
      stepIdx: number,
    ): 'completed' | 'current' | 'upcoming' | 'rejected' | 'cancelled' => {
      if (stepIdx === 3) {
        if (isAccepted) return 'completed';
        if (isRejected) return 'rejected';
        if (isCancelled) return 'cancelled';
      }
      if (isAccepted) return 'completed';
      if (currentIdx > stepIdx) return 'completed';
      if (currentIdx === stepIdx) return 'current';
      return 'upcoming';
    };

    // Sublabel for Step 0 (Candidature)
    const candDate = this.candidacyDate();
    const step0Sublabel = candDate ? `Déposée le ${candDate}` : 'Proposition transmise';

    // Sublabel for Step 1 (Visite & échange)
    const vDate = this.visitDate();
    let step1Sublabel = 'Planification sur site';
    if (vDate) {
      step1Sublabel =
        s === PropertyApplicationStatus.VISITED ||
        s === PropertyApplicationStatus.OFFER_SUBMITTED ||
        isAccepted ||
        isRejected
          ? `Visité le ${vDate}`
          : `RDV le ${vDate}`;
    } else if (s === PropertyApplicationStatus.VISITED) {
      step1Sublabel = 'Bien visité sur site';
    } else if (s === PropertyApplicationStatus.VISIT_CONFIRMED) {
      step1Sublabel = 'Rendez-vous planifié';
    } else if (s === PropertyApplicationStatus.VISIT_INVITED) {
      step1Sublabel = 'Créneaux proposés';
    }

    // Sublabel for Step 2 (Avis de valeur & offre finale)
    const deadlineStr = this.deadlineDate();
    const finalOfferStr = this.finalOfferDate();
    const isFinalOfferPending =
      this.application()?.pendingAction?.actionType === PropertyApplicationPendingActionType.FINAL_OFFER;
    let step2Sublabel = 'Remise de l’avis & de l’offre';
    if (finalOfferStr) {
      step2Sublabel = `Transmise le ${finalOfferStr}`;
    } else if (s === PropertyApplicationStatus.OFFER_SUBMITTED) {
      step2Sublabel = 'Avis et offre transmis';
    } else if (isFinalOfferPending && deadlineStr) {
      step2Sublabel = `Délai : avant le ${deadlineStr}`;
    }

    const hasAppointment = !!this.appointment();

    return [
      {
        index: 0,
        label: 'Candidature',
        sublabel: step0Sublabel,
        dateBadge: candDate,
        icon: 'send',
        state: getStepState(0),
      },
      {
        index: 1,
        label: 'Visite & échange',
        sublabel: step1Sublabel,
        dateBadge: vDate,
        icon: 'meeting_room',
        state: getStepState(1),
        isClickable: hasAppointment,
        clickTooltip: hasAppointment ? 'Cliquer pour afficher les détails du rendez-vous' : undefined,
      },
      {
        index: 2,
        label: 'Avis de valeur & offre finale',
        sublabel: step2Sublabel,
        dateBadge: finalOfferStr
          ? `Transmise le ${finalOfferStr}`
          : s === PropertyApplicationStatus.OFFER_SUBMITTED
            ? null
            : deadlineStr
              ? `Délai : ${deadlineStr}`
              : null,
        icon: 'description',
        state: getStepState(2),
      },
      {
        index: 3,
        label: isAccepted
          ? 'Mandat obtenu'
          : isRejected
            ? 'Non retenue'
            : isCancelled
              ? 'Démarche clôturée'
              : 'Décision vendeur',
        sublabel: isAccepted
          ? 'Mandat accordé'
          : isRejected
            ? 'Sélection clôturée'
            : isCancelled
              ? 'Démarche interrompue'
              : deadlineStr
                ? `Décision avant le ${deadlineStr}`
                : 'Attribution du mandat',
        icon: isAccepted ? 'verified' : isRejected ? 'close' : 'handshake',
        state: getStepState(3),
      },
    ];
  });

  onStepClick(step: ApplicationTimelineStep): void {
    if (step.index === 1 && step.isClickable) {
      this.openVisitDetails();
    }
  }

  openVisitDetails(): void {
    const appt = this.appointment();
    if (!appt) return;

    this.visitClick.emit();
    this.dialog.open(PropertyVisitDetailsDialogComponent, {
      width: '100%',
      maxWidth: '520px',
      data: {
        appointment: appt,
        property: this.property(),
        application: this.application(),
      },
    });
  }
}
